import { Text } from '@thanx/uikit/text'
import ImageUploader from 'components/Form/ImageUploader'
import Input from 'components/Form/Input'
import useFlag from 'hooks/useFlag'
import type { Fields as Campaign } from 'models/Campaign'
import React, { useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { useCampaignConfig } from 'scenes/CampaignCenter/Builder/CampaignConfigContext'
import PushImageControls from 'scenes/CampaignCenter/Builder/components/PushImageControls'
import {
  BODY_MAX_LENGTH,
  SUBTITLE_MAX_LENGTH,
  TITLE_MAX_LENGTH,
} from 'scenes/CampaignCenter/Builder/pushCharLimits'
import UrlPickerGeneric, {
  UrlValue,
} from 'scenes/Cms/components/UrlPickerGeneric'
import { selectCampaignTypeByType } from 'selectors/campaignType'
import { selectMerchant } from 'selectors/merchant'
import { getUrlAddress, getUrlValue } from 'utilities/content/linkUrls'

type Props = {
  campaign: Campaign
}

const PushRedesigned: React.FC<Props> = ({ campaign }) => {
  const campaignType = useSelector(state =>
    selectCampaignTypeByType(state, campaign?.type)
  )
  const merchant = useSelector(state =>
    selectMerchant(state, campaign.merchant_id)
  )
  const { config } = useCampaignConfig()

  const campaignPlaceholders = campaignType?.placeholders
  const msgRequired = useFlag('mx-allow-no-reward-messages', false)
  const showPushImage = useFlag('mx-push-notification-image', false)

  // Subtitle is opt-in (iOS only). Open expanded when editing a campaign that
  // already has a subtitle; "Remove" clears the value and collapses.
  const [showSubtitle, setShowSubtitle] = useState<boolean>(
    !!config.notify_push_subtitle
  )
  const subtitleRef = useRef<any>(null)

  // Local preview state for the hero image upload. The data-URL is persisted
  // into the Formsy model under `notify_push_image` by ImageUploader's
  // `handleUpdate` (setValue) — this state only drives the inline preview.
  const [pushImage, setPushImage] = useState<string | undefined>(undefined)
  const [imageLoading, setImageLoading] = useState(false)
  // True once the merchant clicks the trash icon. Overrides the saved-image
  // fallback below so the preview reverts to the empty "Add image" cell, and is
  // reset whenever a new image is picked.
  const [imageRemoved, setImageRemoved] = useState(false)

  // The hero image already persisted on the campaign (shown when editing and no
  // new image has been picked this session).
  const savedImageUrl = config.notify_push_hero_image_urls?.default

  function handleImageSelected(url?: string) {
    setPushImage(url)
    setImageRemoved(false)
  }

  // Clearing `notify_push_image` (via ImageUploader's controlled `value` prop →
  // withFormsy resync to '') makes the PATCH send an empty value, which the
  // backend treats as "destroy the stored hero image". The removed flag drives
  // the preview back to empty.
  function handleRemoveImage() {
    setPushImage('')
    setImageRemoved(true)
  }

  const previewImageUrl = imageRemoved ? undefined : pushImage || savedImageUrl
  const hasImage = !imageRemoved && (!!pushImage || !!savedImageUrl)

  // Tap deep-link destination. Held as a controlled UrlValue and flattened to a
  // single string in the hidden Formsy field `notify_push_url` via getUrlAddress.
  const [urlValue, setUrlValue] = useState<UrlValue>(
    getUrlValue(config.notify_push_url || '')
  )

  function removeSubtitle() {
    if (subtitleRef.current && subtitleRef.current.clear) {
      subtitleRef.current.clear()
    }
    setShowSubtitle(false)
  }

  return (
    <div>
      <Input
        label={I18n.t('thanx_campaigns.builder.forms.push.title')}
        value={config.notify_push_title || ''}
        name="notify_push_title"
        placeholder={merchant?.nickname || ''}
        counterStyle="bare"
        validations={{ maxLength: TITLE_MAX_LENGTH }}
        validationErrors={{
          maxLength: I18n.t('validations.max_length', {
            maxLength: TITLE_MAX_LENGTH,
          }),
        }}
        helpBlock={
          <em className="font-size-12 grey-50 block margin-top-tiny">
            {I18n.t('thanx_campaigns.builder.forms.push.title_help_text')}
          </em>
        }
      />
      {showSubtitle ? (
        // Vertical breathing room so the "Subtitle (iOS only)" label clears the
        // Title help text above and the "Remove" link clears the Body label below.
        <div className="margin-top-large margin-bottom-large">
          <Input
            label={I18n.t('thanx_campaigns.builder.forms.push.subtitle')}
            value={config.notify_push_subtitle || ''}
            name="notify_push_subtitle"
            counterStyle="bare"
            validations={{ maxLength: SUBTITLE_MAX_LENGTH }}
            validationErrors={{
              maxLength: I18n.t('validations.max_length', {
                maxLength: SUBTITLE_MAX_LENGTH,
              }),
            }}
            innerRef={ref => (subtitleRef.current = ref)}
            helpBlock={
              <em className="font-size-12 grey-50 block margin-top-tiny">
                {I18n.t(
                  'thanx_campaigns.builder.forms.push.subtitle_help_text'
                )}
              </em>
            }
          />
          <button
            type="button"
            onClick={removeSubtitle}
            // Figma "Remove" — BelizeHole-50 (#0073BF) Lato Bold 14, plain link
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              color: '#0073bf',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {I18n.t('thanx_campaigns.builder.forms.push.remove')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowSubtitle(true)}
          // Match the expanded state's vertical spacing so the control doesn't
          // crowd the Title help text above or the Body label below.
          className="margin-top-large margin-bottom-large"
          // Figma "+ Add subtitle" (node 34:1065) — 2px BelizeHole-50 outline,
          // rounded 4px, 7×16 padding, blue Lato Bold 14 + "add" icon.
          style={{
            border: '2px solid #0073bf',
            borderRadius: '4px',
            padding: '7px 16px',
            background: 'transparent',
            color: '#0073bf',
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          <i className="fa fa-plus mr-xs" />
          {I18n.t('thanx_campaigns.builder.forms.push.add_subtitle')}
        </button>
      )}
      <Input
        label={I18n.t('thanx_campaigns.builder.forms.push.text')}
        value={config.notify_push_text || ''}
        name="notify_push_text"
        placeholder={campaignPlaceholders?.notify_push_text}
        counterStyle="bare"
        validations={{ maxLength: BODY_MAX_LENGTH, isRequired: msgRequired }}
        validationErrors={{
          maxLength: I18n.t('validations.max_length', {
            maxLength: BODY_MAX_LENGTH,
          }),
          isRequired: I18n.t('validations.is_required'),
        }}
        componentClass="textarea"
        helpBlock={
          <em className="font-size-12 grey-50 block margin-top-tiny">
            {I18n.t('thanx_campaigns.builder.forms.push.help_text')}
          </em>
        }
      />
      <div className="margin-top-large">
        <Text.BodyText4 className="font-size-14 font-weight-bold mb-xs">
          {I18n.t('thanx_campaigns.builder.forms.push.deep_link_label')}
        </Text.BodyText4>
        <UrlPickerGeneric
          id="notify_push_url"
          type="text"
          url={urlValue}
          required={false}
          onUrlChange={setUrlValue}
        />
        <Input
          type="hidden"
          name="notify_push_url"
          value={getUrlAddress(urlValue)}
        />
        <em className="font-size-12 grey-50 block margin-top-tiny">
          {I18n.t('thanx_campaigns.builder.forms.push.deep_link_help_text')}
        </em>
      </div>
      {showPushImage && (
        <div className="margin-top-large">
          <div className="display-flex flex-align-center mb-xs">
            <Text.BodyText4 className="font-size-14 font-weight-bold">
              {I18n.t('thanx_campaigns.builder.forms.push.image_optional')}
            </Text.BodyText4>
            {hasImage && (
              <Button
                bsStyle="link"
                className="ml-auto padding-none grey-50"
                onClick={handleRemoveImage}
                aria-label={I18n.t(
                  'thanx_campaigns.builder.forms.push.image.remove'
                )}
                title={I18n.t(
                  'thanx_campaigns.builder.forms.push.image.remove'
                )}
              >
                <i className="fa fa-trash" />
              </Button>
            )}
          </div>
          <ImageUploader
            name="notify_push_image"
            fieldName="notify_push_image"
            defaultText={I18n.t(
              'thanx_campaigns.builder.forms.push.image_optional'
            )}
            handleUpdate
            minWidth={200}
            minHeight={200}
            setImageURL={handleImageSelected}
            setIsLoading={setImageLoading}
            value={pushImage}
          >
            <PushImageControls
              imageUrl={previewImageUrl}
              title={I18n.t(
                'thanx_campaigns.builder.forms.push.image_optional'
              )}
              minWidth={200}
              minHeight={200}
              isLoading={imageLoading}
            />
          </ImageUploader>
        </div>
      )}
    </div>
  )
}

export default PushRedesigned
